import { Folder } from "@/models/tasks/folder";
import { FolderUser } from "@/models/tasks/folder-user";
import { Task, TaskStatus } from "@/models/tasks/task";
import { TaskService } from "@/services/task-service";
import { MainSocketService } from "@/services/main-socket-service";

export class TasksState {
	myFolders:FolderUser[] = [];
	sharedFolders:FolderUser[] = [];
	openedFolder:null | FolderUser = null;
	openedTask:null | Task = null;

	showCompletedTasks:boolean = false;
	completedTasks:Task[] = [];
	pendingTasks:Task[] = [];

	draggedTask:Task | null = null;
	draggedTaskWidth:number = 0;
	draggedTaskHeight:number = 0;

	async fetchFolders() {
		let folders = await TaskService.getFolders();
		this.myFolders = folders.filter(f => f.IsOwner);
		this.sharedFolders = folders.filter(f => !f.IsOwner);
	}

	async fetchFolder(folderId:number) {
		if (this.openedFolder) {
			if (folderId == this.openedFolder.ID) {
				return;
			}
			MainSocketService.offAll(this);
		}

		this.completedTasks = [];
		this.pendingTasks = [];
		if (folderId == 0) {
			this.openedFolder = null;
			return;
		}
		let openedFolder = await TaskService.getFolder(folderId);
		MainSocketService.on(`/tasks/folder/${folderId}/task`, (data:any) => this.onFolderTaskUpdate(data), this);
		MainSocketService.on(`/tasks/folder/${folderId}/task/delete`, (data:any) => this.onTaskDelete(data), this);
		let pendingTasks = await TaskService.getTasksByFolderId(folderId);
		let completedTasks = [] as Task[];
		if (this.showCompletedTasks) {
			completedTasks = await TaskService.getCompletedTasksByFolderId(folderId);
		}
		this.openedFolder = openedFolder;
		this.pendingTasks = pendingTasks;
		this.completedTasks = completedTasks;
	}

	async refreshFetchedFolder() {
		if(!this.openedFolder) return;
		let folderId = this.openedFolder.ID;
		this.pendingTasks = await TaskService.getTasksByFolderId(folderId);
		if (this.pendingTasks) {
			this.completedTasks = await TaskService.getCompletedTasksByFolderId(folderId);
		}
	}

	async fetchCompletedTasks() {
		if (!this.openedFolder) return;
		this.showCompletedTasks = true;
		this.completedTasks = await TaskService.getCompletedTasksByFolderId(this.openedFolder.FolderID);
	}

	async hideCompletedTasks() {
		this.showCompletedTasks = false;
		this.completedTasks = [];
	}

	onFolderTaskUpdate(data:any) {
		if (!this.openedFolder) {
			throw new Error("<MEMORY LEAK DETECTED> Not unsubscribed from socket event.");
		}
		let task = new Task(data);
		let originList = this.completedTasks;
		let targetList = this.pendingTasks;
		if (task.Status == TaskStatus.COMPLETED) {
			originList = this.pendingTasks;
			targetList = this.completedTasks;
		}
		let i = originList.findIndex(t => t.ID == task.ID);
		if (i != -1) {
			originList.splice(i, 1);
		}
		i = targetList.findIndex(t => t.ID == task.ID);
		if (i != -1) {
			if (task.FolderID != this.openedFolder.FolderID) {
				targetList.splice(i, 1);
			}
		} else if (task.FolderID == this.openedFolder.FolderID) {
			targetList.push(task);
		}
		if (!this.showCompletedTasks) {
			this.completedTasks = [];
		}
	}

	onTaskDelete(data:any) {
		if (!this.openedFolder) {
			throw new Error("<MEMORY LEAK DETECTED> Not unsubscribed from socket event.");
		}
		let task = new Task(data);
		let i = this.pendingTasks.findIndex(t => t.ID == task.ID);
		if (i != -1) {
			this.pendingTasks.splice(i, 1);
		}
		i = this.completedTasks.findIndex(t => t.ID == task.ID);
		if (i != -1) {
			this.completedTasks.splice(i, 1);
		}
	}

	async fetchTask(taskId:number) {
		if (taskId == 0) {
			this.openedTask = null;
			return;
		}
		if (this.openedTask && taskId == this.openedTask.ID) {
			return;
		}
		this.openedTask = await TaskService.getTask(taskId);
	}

	unsubscribeFromEvents() {
		MainSocketService.offAll(this);
	}

	async completeTask(task:Task) {
		if (!this.openedFolder) return;
		if (this.openedFolder.IsOwner) {
			await TaskService.setTaskStatus(task, TaskStatus.COMPLETED);
			let i = this.pendingTasks.findIndex(t => t.ID == task.ID);
			if (i != -1) {
				this.pendingTasks.splice(i, 1);
			}
			if (this.showCompletedTasks) {
				this.completedTasks.push(task);
				this.completedTasks.sort((a, b) => a.OrderNumber - b.OrderNumber);
			}
		} else {
			await TaskService.setTaskStatus(task, TaskStatus.UNCONFIRMED);
		}
	}

	async setOwnedFoldersOrder(folders:Folder[]) {
		await TaskService.setOwnedFolderOrder(folders);
		this.myFolders = (await TaskService.getFolders()).filter(f => f.IsOwner);
	}

	async setSharedFolderOrder(folders:Folder[]) {
		await TaskService.setSharedFolderOrder(folders);
		this.sharedFolders = (await TaskService.getFolders()).filter(f => !f.IsOwner);
	}

	async deleteFolder(folder:Folder) {
		await TaskService.deleteFolder(folder);
		let i = this.myFolders.findIndex(f => f.FolderID == folder.ID);
		if (i == -1) return;
		this.myFolders.splice(i, 1);
	}

	async moveTaskToFolder(folder:Folder, task:Task) {
		let oldFolderID = task.FolderID;
		task.FolderID = folder.ID;
		await TaskService.putTask(task);
		if (this.openedFolder && this.openedFolder.FolderID == oldFolderID) {
			this.removeTaskFromOpenedFolder(task);
		}
	}

	removeTaskFromOpenedFolder(task:Task) {
		let i = this.pendingTasks.findIndex(t => t.ID == task.ID);
		if (i != -1) {
			this.pendingTasks.splice(i, 1);
		}
		i = this.completedTasks.findIndex(t => t.ID == task.ID);
		if (i != -1) {
			this.completedTasks.splice(i, 1);
		}
	}
}